import { Component, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-voice-input',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './voice-input.component.html',
  styleUrls: ['./voice-input.component.css']
})
export class VoiceInputComponent implements OnDestroy {
  @Input() disabled: boolean = false;
  @Input() existingText: string = '';    // text already in the input field — new voice appends to it
  @Output() textReceived = new EventEmitter<string>();
  @Output() interimText = new EventEmitter<string>();

  isListening = false;
  isSupported = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;

  private recognition: any = null;
  private accumulatedTranscript = '';
  private currentInterim = '';
  private shouldStop = false;
  private silentStop = false;     // true when send button kills recording (no emit)
  private restartToken = 0;

  constructor(private ngZone: NgZone) {}

  toggleListening() {
    if (this.isListening) {
      this.shouldStop = true;
      this.restartToken++;

      if (this.recognition) {
        this.recognition.stop();
      } else {
        this.ngZone.run(() => {
          this.isListening = false;
          const text = (this.accumulatedTranscript + this.currentInterim).trim();
          this.accumulatedTranscript = '';
          this.currentInterim = '';
          this.shouldStop = false;
          if (text && !this.silentStop) this.textReceived.emit(text);
          this.silentStop = false;
        });
      }
    } else {
      this.shouldStop = false;
      this.silentStop = false;
      // Start with whatever text is already in the input field
      this.accumulatedTranscript = this.existingText.trimEnd()
        ? this.existingText.trimEnd() + ' '
        : '';
      this.currentInterim = '';
      this.restartToken++;
      this.createAndStartRecognition();
    }
  }

  /** Called by send button — stops mic silently without emitting textReceived */
  forceStop() {
    if (!this.isListening) return;
    this.silentStop = true;
    this.toggleListening();
  }

  private createAndStartRecognition() {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const myToken = this.restartToken;

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang =
      localStorage.getItem('language') === 'ur' ? 'ur-PK' : 'en-US';

    this.recognition.onresult = (event: any) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          this.accumulatedTranscript += event.results[i][0].transcript + ' ';
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }
      this.currentInterim = interimTranscript;
      const displayText = (this.accumulatedTranscript + interimTranscript).trim();
      this.ngZone.run(() => {
        this.interimText.emit(displayText);
      });
    };

    this.recognition.onerror = (event: any) => {
      if (event.error === 'no-speech' || event.error === 'audio-capture') {
        return;
      }
      this.ngZone.run(() => {
        this.shouldStop = true;
        this.restartToken++;
        this.isListening = false;
        this.recognition = null;
        this.accumulatedTranscript = '';
        this.currentInterim = '';
        this.silentStop = false;
      });
    };

    this.recognition.onend = () => {
      this.recognition = null;

      if (this.shouldStop) {
        this.ngZone.run(() => {
          this.isListening = false;
          const text = (this.accumulatedTranscript + this.currentInterim).trim();
          this.accumulatedTranscript = '';
          this.currentInterim = '';
          this.shouldStop = false;
          const wasSilent = this.silentStop;
          this.silentStop = false;
          if (text && !wasSilent) this.textReceived.emit(text);
        });
      } else if (this.isListening && this.restartToken === myToken) {
        // Browser auto-stopped on silence — restart silently
        const tokenAtRestart = this.restartToken;
        setTimeout(() => {
          if (this.isListening && !this.shouldStop && this.restartToken === tokenAtRestart) {
            this.createAndStartRecognition();
          }
        }, 150);
      }
    };

    try {
      this.recognition.start();
      this.ngZone.run(() => { this.isListening = true; });
    } catch {
      this.ngZone.run(() => {
        this.isListening = false;
        this.recognition = null;
      });
    }
  }

  ngOnDestroy() {
    this.shouldStop = true;
    this.restartToken++;
    if (this.recognition) {
      this.recognition.abort();
      this.recognition = null;
    }
  }
}
